/**
 * transitionMachine — deterministic state machine for the scrub transition
 * lifecycle. Single source of truth so we never silently degrade to fade.
 *
 * States:
 *   idle        — overlay is not mounted / no pending transition
 *   arming      — overlay mounted, video element is loading metadata
 *   ready       — video metadata loaded, scrub can drive currentTime
 *   scrubbing   — user is actively dragging, currentTime is being driven
 *   committing  — release animation toward progress=1 (forward complete)
 *   cancelling  — release animation toward progress=0 (return to origin)
 *   committed   — onComplete fired; awaiting parent to clean up
 *   cancelled   — onCancel   fired; awaiting parent to clean up
 *   failed      — fatal scrub error; parent must fall back to fade with a
 *                 strict `fallback_reason` (see perfLog.FALLBACK_REASONS)
 *
 * Allowed transitions:
 *   idle       → arming
 *   arming     → ready | failed
 *   ready      → scrubbing | committing | cancelling | failed
 *   scrubbing  → committing | cancelling | failed
 *   committing → committed | failed
 *   cancelling → cancelled | failed
 *   committed  → idle
 *   cancelled  → idle
 *   failed     → idle
 */

const ALLOWED = {
  idle:       ["arming"],
  arming:     ["ready", "failed"],
  ready:      ["scrubbing", "committing", "cancelling", "failed"],
  scrubbing:  ["committing", "cancelling", "failed"],
  committing: ["committed", "failed"],
  cancelling: ["cancelled", "failed"],
  committed:  ["idle"],
  cancelled:  ["idle"],
  failed:     ["idle"],
};

export function createTransitionMachine(onChange) {
  let state = "idle";
  const can = (next) => (ALLOWED[state] || []).includes(next);
  return {
    get state() { return state; },
    can,
    /** Move to `next`. Returns true on success, false on illegal transition. */
    go(next, ctx = {}) {
      if (!can(next)) {
        // eslint-disable-next-line no-console
        console.warn(`[transitionFSM] illegal ${state} → ${next}`, ctx);
        return false;
      }
      const prev = state;
      state = next;
      onChange?.(next, prev, ctx);
      return true;
    },
    reset() {
      const prev = state;
      state = "idle";
      if (prev !== "idle") onChange?.("idle", prev, { reason: "reset" });
    },
  };
}
